/**
 * EditableHeroSection - Landing page hero with element-level editing support
 * 
 * Each element (logo, tagline, buttons, badge) is wrapped with SelectableElement
 * for click-to-select in the Page Builder Studio.
 */
import React, { useEffect, useRef, useMemo } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import SelectableElement from './SelectableElement';
import InlineTextEditor from './InlineTextEditor';
import { 
  LANDING_HERO_ELEMENTS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Element animation wrapper
const AnimatedElement = ({ 
  children, 
  motion,
  animationKey,
  disabled,
  className = '' 
}) => {
  const ref = useRef(null);
  const prevKeyRef = useRef(animationKey);
  
  const { durationMs = 700, delayMs = 0, easing = 'smooth', fromOpacity = 0, fromY = 30, fromScale = 1 } = motion || {};
  const easingValue = EASING_VALUES[easing] || EASING_VALUES.smooth;

  // Handle animation replay
  useEffect(() => {
    if (animationKey !== prevKeyRef.current && ref.current) {
      prevKeyRef.current = animationKey;
      // Reset animation
      ref.current.style.opacity = fromOpacity;
      ref.current.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
      
      // Trigger reflow
      void ref.current.offsetWidth;
      
      // Animate to final state
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0) scale(1)';
        }
      }, 50);
    }
  }, [animationKey, fromOpacity, fromY, fromScale]);

  // Initial animation via intersection observer
  useEffect(() => {
    if (disabled || !ref.current) return;
    
    const el = ref.current;
    el.style.opacity = fromOpacity;
    el.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
    el.style.transition = `opacity ${durationMs}ms ${easingValue} ${delayMs}ms, transform ${durationMs}ms ${easingValue} ${delayMs}ms`;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, durationMs, delayMs, easingValue, fromOpacity, fromY, fromScale]);

  if (disabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

const EditableHeroSection = ({ 
  props = {},
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  animationKey = 0,
  disabled = false,
}) => {
  // Get element definitions
  const elements = useMemo(() => {
    const elementsMap = {};
    LANDING_HERO_ELEMENTS.forEach(el => {
      elementsMap[el.id] = el;
    });
    return elementsMap;
  }, []);

  // Get values for each element
  const getValue = (elementId) => {
    const element = elements[elementId];
    if (!element) return '';
    return getElementValue(props, element);
  };

  const getStyles = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementStyles(props, element);
  };

  const getMotion = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementMotion(props, element);
  };

  // Handle element selection
  const handleSelectElement = (elementId) => {
    onSelectElement?.(elementId);
  };

  // Handle inline text editing
  const handleDoubleClick = (elementId, elementType) => {
    if (elementType === 'text' || elementType === 'badge') {
      onStartEditing?.(elementId);
    }
  };

  const handleSaveText = (elementId, newValue) => {
    onUpdateElementValue?.(elementId, newValue);
    onStopEditing?.();
  };

  // Logo element
  const logoValue = getValue('hero.logo');
  const logoStyles = getStyles('hero.logo');
  const logoMotion = getMotion('hero.logo');

  // Early access label
  const labelValue = getValue('hero.early_access_label');
  const labelStyles = getStyles('hero.early_access_label');
  const labelMotion = getMotion('hero.early_access_label');

  // Tagline
  const taglineValue = getValue('hero.tagline');
  const taglineStyles = getStyles('hero.tagline');
  const taglineMotion = getMotion('hero.tagline');

  // Primary CTA
  const primaryCtaValue = getValue('hero.primary_cta');
  const primaryCtaStyles = getStyles('hero.primary_cta');
  const primaryCtaMotion = getMotion('hero.primary_cta');
  const primaryLabel = typeof primaryCtaValue === 'object' ? primaryCtaValue.label : primaryCtaValue;

  // Secondary CTA
  const secondaryCtaValue = getValue('hero.secondary_cta');
  const secondaryCtaStyles = getStyles('hero.secondary_cta');
  const secondaryCtaMotion = getMotion('hero.secondary_cta');
  const secondaryLabel = typeof secondaryCtaValue === 'object' ? secondaryCtaValue.label : secondaryCtaValue;

  // Apply styles to elements
  const getTextStyleClass = (styles) => {
    const sizeMap = {
      'xs': 'text-xs',
      'sm': 'text-sm',
      'base': 'text-base',
      'lg': 'text-lg',
      'xl': 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
    };
    const weightMap = {
      'light': 'font-light',
      'normal': 'font-normal',
      'medium': 'font-medium',
      'semibold': 'font-semibold',
      'bold': 'font-bold',
    };
    const alignMap = {
      'left': 'text-left',
      'center': 'text-center',
      'right': 'text-right',
    };
    
    return `${sizeMap[styles.size] || 'text-base'} ${weightMap[styles.weight] || ''} ${alignMap[styles.alignment] || 'text-center'}`;
  };

  const getImageStyle = (styles) => ({
    transform: `scale(${styles.scale || 1})`,
    opacity: styles.opacity ?? 1,
    filter: styles.glowStrength ? `drop-shadow(0 0 ${styles.glowStrength}px rgba(249, 115, 22, 0.5))` : undefined,
  });

  const getButtonVariantClass = (styles) => {
    const variants = {
      primary: 'bg-orange-500 hover:bg-orange-600 text-white',
      secondary: 'bg-zinc-800 hover:bg-zinc-700 text-white',
      outline: 'border-white/30 text-white hover:bg-white/10 bg-transparent',
      ghost: 'text-white hover:bg-white/10 bg-transparent',
    };
    const paddings = {
      sm: 'px-4 py-2 text-sm',
      md: 'px-6 py-3 text-base',
      lg: 'px-8 py-6 text-lg',
    };
    const radii = {
      none: 'rounded-none',
      sm: 'rounded',
      md: 'rounded-lg',
      lg: 'rounded-xl',
      full: 'rounded-full',
    };
    
    return `${variants[styles.variant] || variants.primary} ${paddings[styles.padding] || paddings.md} ${radii[styles.radius] || radii.md}`;
  };

  return (
    <section className="relative min-h-[90vh] flex flex-col items-center justify-center bg-black overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-orange-950/20 via-black to-black" />
      
      {/* Logo */}
      <AnimatedElement
        motion={logoMotion}
        animationKey={animationKey}
        disabled={disabled}
        className="relative z-10 mb-8"
      >
        <SelectableElement
          elementId="hero.logo"
          elementLabel="Logo"
          elementType="image"
          isSelected={selectedElementId === 'hero.logo'}
          onSelect={handleSelectElement}
          disabled={disabled}
        >
          <img 
            src={logoValue || '/logo-large.png'}
            alt="Trap Comix"
            className="h-32 md:h-48 lg:h-56 w-auto object-contain transition-all"
            style={getImageStyle(logoStyles)}
            onError={(e) => { e.target.src = '/logo.svg'; e.target.className = 'h-24 md:h-32 w-auto'; }}
          />
        </SelectableElement>
      </AnimatedElement>
      
      {/* Early Access Badge */}
      <AnimatedElement
        motion={labelMotion}
        animationKey={animationKey}
        disabled={disabled}
      >
        <SelectableElement
          elementId="hero.early_access_label"
          elementLabel="Early Access Label"
          elementType="badge"
          isSelected={selectedElementId === 'hero.early_access_label'}
          onSelect={handleSelectElement}
          disabled={disabled}
        >
          <span 
            className="inline-block px-4 py-1 bg-orange-500/20 border border-orange-500/40 rounded-full text-orange-400 text-sm font-medium mb-6"
            style={{ opacity: labelStyles.opacity ?? 1 }}
            onDoubleClick={() => handleDoubleClick('hero.early_access_label', 'badge')}
          >
            {editingElementId === 'hero.early_access_label' ? (
              <InlineTextEditor
                value={labelValue}
                isEditing={true}
                maxLength={30}
                onSave={(val) => handleSaveText('hero.early_access_label', val)}
                onCancel={onStopEditing}
                className="inline"
              />
            ) : (
              labelValue || 'Early Access'
            )}
          </span>
        </SelectableElement>
      </AnimatedElement>
      
      {/* Tagline */}
      <AnimatedElement
        motion={taglineMotion}
        animationKey={animationKey}
        disabled={disabled}
        className="max-w-3xl mx-auto text-center px-6 mb-10"
      >
        <SelectableElement
          elementId="hero.tagline"
          elementLabel="Tagline"
          elementType="text"
          isSelected={selectedElementId === 'hero.tagline'}
          onSelect={handleSelectElement}
          disabled={disabled}
        >
          <p 
            className={`text-white/90 leading-relaxed ${getTextStyleClass(taglineStyles)}`}
            style={{ 
              opacity: taglineStyles.opacity ?? 0.9,
              letterSpacing: taglineStyles.letterSpacing ? `${taglineStyles.letterSpacing}px` : undefined,
            }}
            onDoubleClick={() => handleDoubleClick('hero.tagline', 'text')}
          >
            {editingElementId === 'hero.tagline' ? (
              <InlineTextEditor
                value={taglineValue}
                isEditing={true}
                maxLength={200}
                onSave={(val) => handleSaveText('hero.tagline', val)}
                onCancel={onStopEditing}
                className="inline"
              />
            ) : (
              taglineValue || 'Your tagline here...'
            )}
          </p>
        </SelectableElement>
      </AnimatedElement>
      
      {/* CTAs */}
      <AnimatedElement
        motion={primaryCtaMotion}
        animationKey={animationKey}
        disabled={disabled}
        className="flex flex-col sm:flex-row gap-4"
      >
        {/* Primary CTA */}
        <SelectableElement
          elementId="hero.primary_cta"
          elementLabel="Primary CTA"
          elementType="button"
          isSelected={selectedElementId === 'hero.primary_cta'}
          onSelect={handleSelectElement}
          disabled={disabled}
        >
          <Button 
            size="lg" 
            className={getButtonVariantClass(primaryCtaStyles)}
          >
            {primaryLabel || 'Start Creating'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </SelectableElement>
        
        {/* Secondary CTA */}
        <SelectableElement
          elementId="hero.secondary_cta"
          elementLabel="Secondary CTA"
          elementType="button"
          isSelected={selectedElementId === 'hero.secondary_cta'}
          onSelect={handleSelectElement}
          disabled={disabled}
        >
          <Button 
            variant="outline" 
            size="lg"
            className={getButtonVariantClass(secondaryCtaStyles)}
          >
            {secondaryLabel || 'Explore Platform'}
          </Button>
        </SelectableElement>
      </AnimatedElement>
      
      {/* Scroll indicator */}
      <AnimatedElement
        motion={{ durationMs: 700, delayMs: 600, easing: 'smooth', fromOpacity: 0, fromY: 20 }}
        animationKey={animationKey}
        disabled={disabled}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ChevronDown className="w-8 h-8 text-white/40 animate-bounce" />
      </AnimatedElement>
    </section>
  );
};

export default EditableHeroSection;
